import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Box,
    Chip,
    FormControlLabel,
    Switch,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import Logger from "../../../services/Logger";
import StatsJigsawService from "../../../services/StatsJigsawService";
import JigsawPlayerStatsDialog from "./JigsawPlayerStatsDialog";

const PAGE_STORAGE_KEY = "admin.jigsaw.topUsers.page";
const PAGE_SIZE_STORAGE_KEY = "admin.jigsaw.topUsers.pageSize";
const EXCLUDE_TEST_STORAGE_KEY = "admin.jigsaw.topUsers.excludeTest";
const EXCLUDE_STAFF_STORAGE_KEY = "admin.jigsaw.topUsers.excludeStaff";
const NOTIFICATIONS_STORAGE_KEY = "admin.jigsaw.topUsers.notifications";
const PAGE_SIZE_OPTIONS = [15, 25, 50];
const DEFAULT_PAGE_SIZE = 25;
const NOTIFICATION_FILTERS = ["all", "on", "off"];

const formatNumber = (value) => (Number.isFinite(Number(value)) ? Number(value).toLocaleString() : "—");

const formatDateTime = (epochMillis) =>
    epochMillis ? dayjs(Number(epochMillis)).format("YYYY-MM-DD HH:mm") : "—";

function persistItem(key, value) {
    try {
        window.localStorage.setItem(key, value);
    } catch {
        /* private mode */
    }
}

function loadStoredBool(key, fallback) {
    try {
        const stored = window.localStorage.getItem(key);
        if (stored === "true") return true;
        if (stored === "false") return false;
    } catch {
        /* private mode */
    }
    return fallback;
}

function loadStoredPage() {
    try {
        const stored = parseInt(window.localStorage.getItem(PAGE_STORAGE_KEY), 10);
        if (Number.isFinite(stored) && stored >= 0) return stored;
    } catch {
        /* private mode */
    }
    return 0;
}

function loadStoredPageSize() {
    try {
        const stored = parseInt(window.localStorage.getItem(PAGE_SIZE_STORAGE_KEY), 10);
        if (PAGE_SIZE_OPTIONS.includes(stored)) return stored;
    } catch {
        /* private mode */
    }
    return DEFAULT_PAGE_SIZE;
}

function persistPageModel(page, pageSize) {
    persistItem(PAGE_STORAGE_KEY, String(page));
    persistItem(PAGE_SIZE_STORAGE_KEY, String(pageSize));
}

function loadStoredNotifications() {
    try {
        const stored = window.localStorage.getItem(NOTIFICATIONS_STORAGE_KEY);
        if (NOTIFICATION_FILTERS.includes(stored)) return stored;
    } catch {
        /* private mode */
    }
    return "all";
}

export default function JigsawTopUsersPanel({ language }) {
    const [excludeTest, setExcludeTest] = useState(() => loadStoredBool(EXCLUDE_TEST_STORAGE_KEY, true));
    const [excludeStaff, setExcludeStaff] = useState(() => loadStoredBool(EXCLUDE_STAFF_STORAGE_KEY, true));
    const [notifications, setNotifications] = useState(loadStoredNotifications);
    const [pageModel, setPageModel] = useState(() => ({
        page: loadStoredPage(),
        pageSize: loadStoredPageSize(),
    }));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [selectedPlayer, setSelectedPlayer] = useState(null);

    const applyPageModel = useCallback((next) => {
        setPageModel(next);
        persistPageModel(next.page, next.pageSize);
    }, []);

    const previousLanguage = useRef(language);
    useEffect(() => {
        if (previousLanguage.current === language) return;
        previousLanguage.current = language;
        setPageModel((prev) => {
            if (prev.page === 0) return prev;
            const next = { ...prev, page: 0 };
            persistPageModel(next.page, next.pageSize);
            return next;
        });
    }, [language]);

    const resetPage = useCallback((update) => {
        update();
        setPageModel((prev) => {
            const next = prev.page === 0 ? prev : { ...prev, page: 0 };
            persistPageModel(next.page, next.pageSize);
            return next;
        });
    }, []);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await StatsJigsawService.topUsers({
                    language,
                    page: pageModel.page,
                    pageSize: pageModel.pageSize,
                    includeTest: !excludeTest,
                    excludeStaff,
                    notifications,
                });
                if (!cancelled) {
                    setResult(data ?? null);
                }
            } catch (err) {
                Logger.error("Failed to load jigsaw top users:", err);
                if (!cancelled) {
                    setError("Failed to load top users.");
                    setResult(null);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [language, excludeTest, excludeStaff, notifications, pageModel.page, pageModel.pageSize]);

    useEffect(() => {
        const total = Number(result?.total);
        if (!Number.isFinite(total) || total < 0) return;
        const maxPage = Math.max(0, Math.ceil(total / pageModel.pageSize) - 1);
        if (pageModel.page > maxPage) {
            applyPageModel({ page: maxPage, pageSize: pageModel.pageSize });
        }
    }, [result, pageModel.page, pageModel.pageSize, applyPageModel]);

    const rows = useMemo(() => (Array.isArray(result?.users) ? result.users : []), [result]);
    const rowCount = Number(result?.total) || 0;

    const columns = useMemo(
        () => [
            {
                field: "rank",
                headerName: "Rank",
                width: 80,
                sortable: false,
            },
            {
                field: "type",
                headerName: "Type",
                width: 130,
                sortable: false,
                renderCell: (params) =>
                    params.value === "SIGNED" ? (
                        <Chip size="small" color="primary" label="Signed in" />
                    ) : (
                        <Chip size="small" variant="outlined" label="Anonymous" />
                    ),
            },
            {
                field: "name",
                headerName: "Player",
                flex: 1,
                minWidth: 180,
                sortable: false,
                valueGetter: (value, row) => value || row?.email || "—",
            },
            {
                field: "email",
                headerName: "Email",
                flex: 1,
                minWidth: 200,
                sortable: false,
                valueGetter: (value) => value || "—",
            },
            {
                field: "score",
                headerName: "Score",
                width: 110,
                sortable: false,
                valueFormatter: (value) => formatNumber(value),
            },
            {
                field: "started",
                headerName: "Started",
                width: 110,
                sortable: false,
                valueFormatter: (value) => formatNumber(value),
            },
            {
                field: "completed",
                headerName: "Completed",
                width: 120,
                sortable: false,
                valueFormatter: (value) => formatNumber(value),
            },
            {
                field: "notificationsEnabled",
                headerName: "Notification",
                width: 130,
                sortable: false,
                renderCell: (params) => {
                    if (params.row?.type !== "SIGNED" || params.value == null) {
                        return "—";
                    }
                    return params.value ? (
                        <Chip size="small" color="success" label="On" />
                    ) : (
                        <Chip size="small" label="Off" />
                    );
                },
            },
            {
                field: "updatedAt",
                headerName: "Updated",
                width: 170,
                sortable: false,
                valueFormatter: (value) => formatDateTime(value),
            },
        ],
        []
    );

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={excludeTest}
                            onChange={(event) =>
                                resetPage(() => {
                                    const checked = event.target.checked;
                                    setExcludeTest(checked);
                                    persistItem(EXCLUDE_TEST_STORAGE_KEY, checked ? "true" : "false");
                                })
                            }
                        />
                    }
                    label="Exclude test accounts"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={excludeStaff}
                            onChange={(event) =>
                                resetPage(() => {
                                    const checked = event.target.checked;
                                    setExcludeStaff(checked);
                                    persistItem(EXCLUDE_STAFF_STORAGE_KEY, checked ? "true" : "false");
                                })
                            }
                        />
                    }
                    label="Exclude staff"
                />

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2">Notification</Typography>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={notifications}
                        onChange={(event, value) => {
                            if (!value) return;
                            resetPage(() => {
                                setNotifications(value);
                                persistItem(NOTIFICATIONS_STORAGE_KEY, value);
                            });
                        }}
                        aria-label="Notification filter"
                    >
                        <ToggleButton value="all" sx={{ textTransform: "none", px: 1.5, minHeight: 40 }}>
                            All
                        </ToggleButton>
                        <ToggleButton value="on" sx={{ textTransform: "none", px: 1.5, minHeight: 40 }}>
                            On
                        </ToggleButton>
                        <ToggleButton value="off" sx={{ textTransform: "none", px: 1.5, minHeight: 40 }}>
                            Off
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Highest jigsaw score first. Signed-in players use the leaderboard (1 point per completed
                puzzle); guests are ranked by completed puzzles. Started is play sessions begun; Completed
                is sessions finished.
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {!error && result && (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                        gap: 1,
                        mb: 2,
                    }}
                >
                    <SummaryTile
                        label="Players"
                        value={formatNumber(result.total)}
                        hint={notifications === "all" ? "Signed-in and guests" : "Matching this notification filter"}
                    />
                    <SummaryTile label="Signed in" value={formatNumber(result.signedCount)} />
                    <SummaryTile
                        label="Anonymous"
                        value={formatNumber(result.guestCount)}
                        hint={notifications === "all" ? "Guest sessions" : "Guests have no notification setting"}
                    />
                </Box>
            )}

            <DataGrid
                rows={rows}
                columns={columns}
                getRowId={(row) => row.id}
                rowCount={rowCount}
                loading={loading}
                paginationMode="server"
                paginationModel={pageModel}
                onPaginationModelChange={applyPageModel}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                autoHeight
                disableRowSelectionOnClick
                hideFooterSelectedRowCount
                onRowClick={(params) => {
                    if (params?.row?.cloudUserId || params?.row?.sessionId) {
                        setSelectedPlayer(params.row);
                    }
                }}
                sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
                localeText={{ noRowsLabel: loading ? "Loading…" : "No players yet." }}
            />
            <JigsawPlayerStatsDialog
                open={Boolean(selectedPlayer)}
                onClose={() => setSelectedPlayer(null)}
                player={selectedPlayer}
                language={language}
            />
        </Box>
    );
}

function SummaryTile({ label, value, hint }) {
    return (
        <Box sx={{ p: 1.25, border: 1, borderColor: "divider", borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
                {label}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {value}
            </Typography>
            {hint ? (
                <Typography variant="caption" color="text.secondary">
                    {hint}
                </Typography>
            ) : null}
        </Box>
    );
}
